/* Analýza V2+ — nefinanční majetek podle typu.
 *
 * PROČ TO EXISTUJE (nález Owner UAT):
 * Formulář nabízel u KAŽDÉ položky nefinančního majetku pole „měsíční příjem“.
 * U pronajímaného bytu to dává smysl. U auta, hodinek a zlata to nedává smysl
 * vůbec — poradce se na schůzce musel klienta ptát, kolik mu měsíčně vynáší
 * snubní prsten, nebo pole přeskakovat a doufat, že si toho klient nevšimne.
 *
 * PRAVIDLO MODULU:
 * Pole „měsíční příjem“ existuje POUZE u výnosových aktiv — pronájem, komerční
 * prostory, firma. Nikde jinde. Sada polí se odvozuje z maPrijem(typ), takže
 * to nejde rozejít úpravou jednoho místa.
 *
 * POZOR — jako v model.js: FUNKCE beroucí měnu parametrem, ne konstanta.
 */

import { MENY } from "./meny";

export const NEMOVITOST_BYDLENI = "nemovitost_bydleni";
export const NEMOVITOST_PRONAJEM = "nemovitost_pronajem";
export const KOMERCNI = "komercni";
export const POZEMEK = "pozemek";
export const VOZIDLO = "vozidlo";
export const CENNOSTI = "cennosti";
export const FIRMA = "firma";
export const BUDOUCI = "budouci";

export const TYPY_NEFIN = [
  NEMOVITOST_BYDLENI,
  NEMOVITOST_PRONAJEM,
  KOMERCNI,
  POZEMEK,
  VOZIDLO,
  CENNOSTI,
  FIRMA,
  BUDOUCI,
];

const LABEL = {
  cs: {
    [NEMOVITOST_BYDLENI]: "Nemovitost k bydlení",
    [NEMOVITOST_PRONAJEM]: "Nemovitost k pronájmu",
    [KOMERCNI]: "Komerční prostory",
    [POZEMEK]: "Pozemek / les / pole",
    [VOZIDLO]: "Vozidlo",
    [CENNOSTI]: "Cennosti (hodinky, umění, zlato)",
    [FIRMA]: "Firma / podíl v podnikání",
    [BUDOUCI]: "Budoucí majetek (dědictví, dar)",
  },
  sk: {
    [NEMOVITOST_BYDLENI]: "Nehnuteľnosť na bývanie",
    [NEMOVITOST_PRONAJEM]: "Nehnuteľnosť na prenájom",
    [KOMERCNI]: "Komerčné priestory",
    [POZEMEK]: "Pozemok / les / pole",
    [VOZIDLO]: "Vozidlo",
    [CENNOSTI]: "Cennosti (hodinky, umenie, zlato)",
    [FIRMA]: "Firma / podiel v podnikaní",
    [BUDOUCI]: "Budúci majetok (dedičstvo, dar)",
  },
};

/* ── DRUH majetku (hrubší škatulka pro filtr v seznamu klientů) ───────
 *
 * PROČ DVĚ OSY:
 * `typ` (osm hodnot výše) říká, co to přesně je — podle něj se kreslí pole
 * a popisky. Poradce ale v seznamu klientů hledá „kdo má nemovitost", ne
 * „kdo má nemovitost k pronájmu". Filtr proto jede na hrubším `druhu`.
 *
 * TAHLE MAPA JE JEDINÝ ZDROJ PRAVDY. Backend `kind` jen ověří proti seznamu
 * a uloží — schválně si ho neodvozuje podruhé. Dvě kopie téhle mapy by se
 * časem rozešly a filtr by ukazoval něco jiného než složka.
 */
export const DRUH_NEMOVITOST = "nemovitost";
export const DRUH_POZEMEK = "pozemek";
export const DRUH_VOZIDLO = "vozidlo";
export const DRUH_CENNOSTI = "cennosti";
export const DRUH_FIRMA = "firma";
export const DRUH_DEDICTVI = "dedictvi";
export const DRUH_OSTATNI = "ostatni";

export const DRUHY_MAJETKU = [
  DRUH_NEMOVITOST, DRUH_POZEMEK, DRUH_VOZIDLO,
  DRUH_CENNOSTI, DRUH_FIRMA, DRUH_DEDICTVI, DRUH_OSTATNI,
];

const DRUH_PODLE_TYPU = {
  [NEMOVITOST_BYDLENI]: DRUH_NEMOVITOST,
  [NEMOVITOST_PRONAJEM]: DRUH_NEMOVITOST,
  [KOMERCNI]: DRUH_NEMOVITOST,
  [POZEMEK]: DRUH_POZEMEK,
  [VOZIDLO]: DRUH_VOZIDLO,
  [CENNOSTI]: DRUH_CENNOSTI,
  [FIRMA]: DRUH_FIRMA,
  [BUDOUCI]: DRUH_DEDICTVI,
};

const DRUH_LABEL = {
  cs: {
    [DRUH_NEMOVITOST]: "Nemovitost",
    [DRUH_POZEMEK]: "Půda a lesy",
    [DRUH_VOZIDLO]: "Vozidlo",
    [DRUH_CENNOSTI]: "Cennosti a zlato",
    [DRUH_FIRMA]: "Firemní podíl",
    [DRUH_DEDICTVI]: "Dědictví",
    [DRUH_OSTATNI]: "Ostatní majetek",
  },
  sk: {
    [DRUH_NEMOVITOST]: "Nehnuteľnosť",
    [DRUH_POZEMEK]: "Pôda a lesy",
    [DRUH_VOZIDLO]: "Vozidlo",
    [DRUH_CENNOSTI]: "Cennosti a zlato",
    [DRUH_FIRMA]: "Firemný podiel",
    [DRUH_DEDICTVI]: "Dedičstvo",
    [DRUH_OSTATNI]: "Ostatný majetok",
  },
};

/** Druh majetku pro filtr. Neznámý typ spadne do „ostatní", nikdy do prázdna. */
export function druhMajetku(typ) {
  return DRUH_PODLE_TYPU[String(typ ?? "").trim()] || DRUH_OSTATNI;
}

export function druhLabel(druh, lang = "cs") {
  const t = DRUH_LABEL[lang] || DRUH_LABEL.cs;
  return t[String(druh ?? "").trim()] || "";
}

/** Nabídka druhů do filtru: [{ k, l }]. */
export function nabidkaDruhu(lang = "cs") {
  return DRUHY_MAJETKU.map((k) => ({ k, l: druhLabel(k, lang) }));
}

/* Výnosová aktiva — jediná, u kterých má smysl ptát se na měsíční příjem.
 * Auto, hodinky ani pozemek nic měsíčně nenesou; pozemek se sice pronajmout
 * dá, ale pak ho poradce zapíše jako komerční, ne jako pozemek. */
const VYNOSOVA = new Set([NEMOVITOST_PRONAJEM, KOMERCNI, FIRMA]);

/**
 * Vynáší tento typ majetku měsíčně? Řídí zobrazení pole „měsíční příjem“.
 * Neznámý typ → false. Radši pole nezobrazit než se ptát na nesmysl.
 */
export function maPrijem(typ) {
  return VYNOSOVA.has(String(typ ?? "").trim());
}

/* NÁJEMNÍ TYPY (14. 10. 2026) — u nich je „příjem“ HRUBÝ NÁJEM, tedy to, co
 * nájemník měsíčně platí (bez energií), PŘED náklady. Tak to pole vždycky
 * fungovalo: vedle něj stojí samostatné pole „Měsíční náklady“ a nikde
 * v appce se od něj nic neodečítá. Mění se jen POPISEK, klíč `prijem`
 * i význam uložených čísel zůstávají. Firma má dál obecný „Měsíční příjem“.
 * Kalkulačka Pronájem (lib/nastroje/pronajemNastroj.js) bere tytéž typy. */
export const NAJEMNI_TYPY = Object.freeze([NEMOVITOST_PRONAJEM, KOMERCNI]);
export function jeNajemni(typ) {
  return NAJEMNI_TYPY.includes(String(typ ?? "").trim());
}
/** Popisky pole `prijem`: nájemní typy × ostatní výnosová aktiva. */
export const POPISEK_PRIJMU = Object.freeze({ najem: "Hrubý nájem", obecny: "Měsíční příjem" });

export function nefinLabel(typ, lang = "cs") {
  const t = LABEL[lang] || LABEL.cs;
  return t[String(typ ?? "").trim()] || "";
}

/** Nabídka typů do výběru: [{ k, l }]. */
export function nabidkaTypu(lang = "cs") {
  return TYPY_NEFIN.map((k) => ({ k, l: nefinLabel(k, lang) }));
}

/* ── Stavebnice polí (stejný tvar jako produktyPole.js) ──────────── */
const text = (k, l, extra = {}) => ({ k, l, type: "text", ...extra });
const cislo = (k, l, suffix, extra = {}) => ({ k, l, type: "number", suffix, ...extra });
const datum = (k, l) => ({ k, l, type: "date" });
const vyber = (k, l, moznosti) => ({ k, l, type: "select", moznosti });
const zaskrtnout = (k, l) => ({ k, l, type: "check" });

export const JISTOTA = ["Jisté", "Pravděpodobné", "Nejisté"];

/* Specifika typu — bez společné části (označení, hodnota, příjem, měna, poznámka). */
function specifika(typ, c) {
  switch (typ) {
    case NEMOVITOST_BYDLENI:
      return [text("adresa", "Adresa"), datum("porizeno", "Datum pořízení"), zaskrtnout("pojisteno", "Pojištěno")];
    case NEMOVITOST_PRONAJEM:
      return [
        text("adresa", "Adresa"),
        cislo("naklady", "Měsíční náklady", c ? `${c}/m` : ""),
        datum("najemDo", "Nájemní smlouva do"),
        zaskrtnout("pojisteno", "Pojištěno"),
      ];
    case KOMERCNI:
      return [
        text("adresa", "Adresa"),
        cislo("naklady", "Měsíční náklady", c ? `${c}/m` : ""),
        datum("najemDo", "Nájemní smlouva do"),
        zaskrtnout("pojisteno", "Pojištěno"),
      ];
    case POZEMEK:
      return [text("katastr", "Katastrální území"), cislo("vymera", "Výměra", "m²")];
    case VOZIDLO:
      return [text("spz", "SPZ"), cislo("rokVyroby", "Rok výroby", ""), zaskrtnout("pojisteno", "Havarijní pojištění")];
    case CENNOSTI:
      return [text("popis", "Popis", { wide: true }), zaskrtnout("pojisteno", "Pojištěno")];
    case FIRMA:
      return [text("ico", "IČO"), cislo("podil", "Podíl", "%")];
    case BUDOUCI:
      return [datum("ocekavano", "Očekáváno kdy"), vyber("jistota", "Jak jisté to je", JISTOTA)];
    default:
      return [];
  }
}

/**
 * Sada polí pro daný typ nefinančního majetku.
 *
 * Pole „prijem“ se přidává VÝHRADNĚ podle maPrijem(typ) — proto se nedá
 * omylem dostat k autu nebo k hodinkám.
 */
export function poleNefin(typ, cur) {
  const t = String(typ ?? "").trim();
  const c = String(cur ?? "").trim();
  const mesicne = c ? `${c}/m` : "";
  const pole = [text("oznaceni", "Označení"), ...specifika(t, c), cislo("hodnota", "Odhadovaná hodnota", c)];
  if (maPrijem(t)) pole.push(cislo("prijem", jeNajemni(t) ? POPISEK_PRIJMU.najem : POPISEK_PRIJMU.obecny, mesicne));
  pole.push(vyber("mena", "Měna", MENY), text("pozn", "Poznámka", { wide: true }));
  return pole;
}

/** Klíče polí typu — pro kontrolu úplnosti a testy. */
export const kliceNefin = (typ, cur = "Kč") => poleNefin(typ, cur).map((p) => p.k);

export const znamyTyp = (typ) => TYPY_NEFIN.includes(String(typ ?? "").trim());
